import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import JSZip from "jszip";

import CloseIcon from "@mui/icons-material/Close";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";

import { useUserAuth } from "../../contexts/authContext";

const ZeroConflictAnalysisReport = ({ selectedReport, setSelectedReport }) => {
    const [data, setData] = useState([]);
    const [images, setImages] = useState([]);

    const { getOrganizationByID } = useUserAuth();

    useEffect(() => {
        handleExtractFileData();
    }, []);

    const handleExtractFileData = async () => {
        try {
            const zip = await JSZip.loadAsync(selectedReport.report_file.data);
            const imageList = [];

            for (const file of Object.keys(zip.files)) {
                if (file.endsWith(".json")) {
                    const jsonString = await zip.file(file).async("string");
                    const jsonData = JSON.parse(jsonString);
                    const organization = getOrganizationByID(jsonData.organization_id);

                    const updatedData = [
                        { title: "Time Start", value: dayjs(jsonData.time_start).format("MMMM D, YYYY [at] h:mm a") },
                        { title: "Time End", value: dayjs(jsonData.time_end).format("MMMM D, YYYY [at] h:mm a") },
                        { title: "Pilot UUID", value: jsonData.pilot_uuid },
                        { title: "Organization", value: organization ? organization.name : "Unknown" },
                        { title: "Number of Flights", value: `${jsonData.num_flights}` },
                        { title: "Caution Events", value: `${jsonData.caution_events}` },
                        { title: "Warning Events", value: `${jsonData.warning_events}` },
                        { title: "Average Encounter Duration", value: `${jsonData.avg_encounter_duration_sec} seconds` },
                        { title: "Mean Time Between Alerts", value: `${jsonData.mean_time_between_alerts_hr} hours` }
                    ];
                    setData(updatedData);
                }
                if (file.endsWith(".png")) {
                    const blob = await zip.file(file).async("blob");
                    const imageUrl = URL.createObjectURL(blob);
                    imageList.push({ name: file, url: imageUrl });
                }
            }
            const preferredImageOrder = {
                "zca_ownship_intruder_scatter.png": 1,
                "caution_event_heatmap.png": 2,
                "warning_event_heatmap.png": 3
            };
            const sortedImageList = imageList
                .filter(({ name }) => {
                    return name in preferredImageOrder;
                })
                .sort((a, b) => {
                    return preferredImageOrder[a.name] - preferredImageOrder[b.name];
                });
            setImages(sortedImageList);
        } catch (error) {
            console.error("Error unzipping file:", error);
        }
    };
    return (
        <Paper sx={{ p: 2, m: 2, display: "flex", flexDirection: "column", gap: 2, maxWidth: 1200, margin: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography variant="h6">Zero Conflict Analysis Report</Typography>
                    <Typography color={"text.secondary"}>{dayjs(selectedReport.date_updated).format("MMMM D, YYYY [at] h:mm a")}</Typography>
                </Box>

                <Tooltip title="Close">
                    <IconButton onClick={() => setSelectedReport(null)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                        <Typography variant="h6" gutterBottom>{`Report Information`}</Typography>

                        <Grid container spacing={1}>
                            {data.map(({ title, value }, i) => (
                                <Grid key={i} size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body1" gutterBottom noWrap>
                                        {title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {value}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {images.map((image, i) => (
                    <Grid key={i} size={{ xs: 12, md: 6 }}>
                        <Typography variant="h6" gutterBottom>
                            {image.name}
                        </Typography>
                        <Box
                            component="img"
                            sx={{ width: "100%", height: "auto", objectFit: "contain", maxHeight: "300px" }}
                            src={image.url}
                            alt={image.name}
                        />
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

export default ZeroConflictAnalysisReport;
